import { Typography } from '../../../components/typography/typography';
import { ProductCard } from '../product-card';
import { Location } from '../../../clients/quipli-service/location/location';
import { CustomerProduct } from '../../../clients/customer-api/product/customer-product';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Carousel } from '../../../components/carousel/carousel';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { useEffect, useState } from 'react';
import { Label } from '../../../clients/quipli-service/label/label';
import { Dictionary, keyBy } from 'lodash';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import {
  CalculateSalePriceResult,
  ErrorCalculatePriceResult,
  RentalProductPrice,
  SaleProductPrice,
} from '../../../clients/quipli-service/pricing/pricing';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { EcommerceUser } from '../product-details/product-details';

export const FeaturedProducts = ({
  location,
  user,
}: {
  location: Location & { urlAlias: string };
  user: EcommerceUser;
}) => {
  const [featuredProducts, setFeaturedProducts] = useState<CustomerProduct[]>([]);
  const [labels, setLabels] = useState<Dictionary<Label>>({});
  const [configs, setConfigs] = useState<Dictionary<Configuration>>({});
  const [rentalPrices, setRentalPrices] = useState<Map<number, RentalProductPrice[]>>(new Map());
  const [salePrices, setSalePrices] = useState<Dictionary<SaleProductPrice>>({});
  const { getFeaturedProducts, getMultipleLabels, getConfigurations, listPricesForMultipleProducts, calculatePrices } =
    useCustomerAPI();

  useEffect(() => {
    const init = async () => {
      const [fp, l, c] = await Promise.all([
        getFeaturedProducts(),
        getMultipleLabels(['L_BUILD_PRODUCT']),
        getConfigurations([
          'CONF_USER_UNAPPROVED_HIDE_PRICES',
          'CONF_CURRENCY',
          'CONF_ECOMMERCE_SALE_REQUIRE_IN_STOCK',
          'CONF_SHOW_PRICES_WITH_TAX',
          'CONF_PRICE_TAX_LABEL',
        ]),
      ]);

      const saleItems = fp.filter((item) => item.allowSale);
      const rentalItems = fp.filter((item) => item.allowRent);

      const [rp, sp] = await Promise.all([
        rentalItems.length
          ? listPricesForMultipleProducts(
              rentalItems.map((p) => p.id),
              location.id,
              OrderType.Rental,
              user.userId,
            )
          : new Map(),
        saleItems.length
          ? calculatePrices(
              saleItems.map((p) => ({
                productId: p.id,
                type: OrderType.Sale,
                locationId: location.id,
                userId: user.userId,
              })),
            )
          : [],
      ]);
      setFeaturedProducts(fp);
      setLabels(keyBy(l, 'key'));
      setConfigs(keyBy(c, 'variable'));
      setRentalPrices(rp as Map<number, RentalProductPrice[]>);
      setSalePrices(
        keyBy(
          sp
            .filter((res) => !(res as ErrorCalculatePriceResult).error)
            .map((res) => (res as CalculateSalePriceResult).unitPrice) as SaleProductPrice[],
          'productId',
        ),
      );
    };

    init();
  }, []);

  return featuredProducts.length > 0 ? (
    <div className="">
      <Typography type="display-sm" weight="bold">
        Frequently rented with this item
      </Typography>
      <div className="px-0 block mt-3">
        <Carousel className="">
          {featuredProducts?.map((product) => {
            return (
              <div className="">
                <ProductCard
                  key={product.id}
                  product={product}
                  location={location}
                  isLoggedIn={user.isLoggedIn}
                  labels={labels}
                  configs={configs}
                  rentalPrices={rentalPrices.get(product.id)}
                  salePrice={salePrices[product.id]?.price}
                  saleTax={salePrices[product.id]?.taxPercentage}
                />
              </div>
            );
          })}
        </Carousel>
      </div>
    </div>
  ) : (
    <></>
  );
};
